import { cached } from '@glimmer/tracking';

import type { DateRangeInterface, DSData, DSSetup } from 'webapp-types';
import { formatNumber, sumValues } from 'webapp/utils/number';

import type DataSelect from 'webapp/models-custom/data-select';
import type DsDataOutputBase from 'webapp/models-custom/ds-data/output/base';
import type DsDataOutputOther from 'webapp/models-custom/ds-data/output/other';
import StatusObject from 'webapp/models-custom/status-object';

// DsData/* namespace classes are just helpers to wrap and enrich data (sample) returned from BE
// They join info from DataSelect (e.g. metric, compare settings, overrides) and the data itself
// DsData models are used by Visualization models to group and transform the data in preparation
// for DChartSeries and DChartPoint creation, that are in turn used when rendering data.
// Read more: https://databox.atlassian.net/wiki/x/AgAb3Q

export default abstract class DsDataRootBase {
	dataSelect: DataSelect;

	outputs: DsDataOutputBase[] = []; // set in extended
	outputsOther: DsDataOutputOther[] = []; // set in extended

	metricName: string;
	source: DSData.Root['source'];
	format: string;
	scale: number | null = null;
	invert: boolean;
	lastSync: number;
	granulationLabels: DSData.GranulationLabel[];
	statusObjects: StatusObject[];
	dateRange: DateRangeInterface.FormulaInterface;
	isSourceDataset: boolean;
	isAdvancedFilteringSupported: boolean;
	allowAdvancedFilters: boolean;

	dimensions: DSSetup.DimensionItem[] = []; // only set on top-level root (RootCompare has none)

	constructor(dataSelect: DataSelect, rawData: DSData.Root) {
		this.dataSelect = dataSelect;
		this.metricName = rawData.metricName;
		this.source = rawData.source;
		this.format = rawData.format;
		this.invert = !!rawData.invert;
		this.lastSync = rawData.lastSync;
		this.dateRange = dataSelect.dateRange;
		this.isSourceDataset = !!rawData.source?.isDatasets;
		this.isAdvancedFilteringSupported = !!rawData.isAdvancedFilteringSupported;
		this.allowAdvancedFilters =
			rawData.allowAdvancedFilters ?? this.isAdvancedFilteringSupported; // fallback for old payloads

		if (typeof (rawData as DSData.RootTop).scale === 'number') {
			this.scale = (rawData as DSData.RootTop).scale;
		}

		this.granulationLabels = (rawData.granulationLabels || []).map((granulationLabelData) => {
			return {
				ts: granulationLabelData.ts,
				endTs: granulationLabelData.endTs,
				label: granulationLabelData.label,
				labelShort: granulationLabelData.labelShort,
				labelLong: granulationLabelData.labelLong,
			};
		});

		// creating statusObjects
		const statusObjects = StatusObject.createFromDataArray(rawData.statusObjects);
		statusObjects.forEach((so) => {
			so.dataLocation = this.granulationLabels.length > 1 ? 'series' : 'number';
			so.blockId = dataSelect.blockId;
		});
		this.statusObjects = statusObjects;
	}

	getDimensionLabel(key: string): string | undefined {
		return this.dimensionLabelsByKey.get(key);
	}

	@cached
	private get dimensionLabelsByKey(): Map<string, string> {
		return new Map(this.dimensions.map((d) => [d.key, d.value]));
	}

	get isDataIncomplete() {
		return this.statusObjects.some((so) => so.isSync);
	}

	// for media creatives images loading status in table
	get isMediaLoading() {
		return this.statusObjects.some((so) => so.isMediaSync);
	}

	get sourceName() {
		return this.source?.name || '';
	}

	get sourceAvatar() {
		return this.source?.avatar || '';
	}

	@cached
	get isDiffAttributes() {
		return this.outputs.flatMap((item) => item.attributeNames).length > 1;
	}

	@cached
	get isDiffUnits() {
		return this.outputs.map((item) => item.unit).length > 1;
	}

	get isSameAttributeAndUnit() {
		return !this.isDiffAttributes && !this.isDiffUnits;
	}

	@cached
	get totalSumValue(): number | null {
		return sumValues(this.outputs.map((o) => o.totalSumValue));
	}

	@cached
	get totalSumValueFormatted(): string {
		let valueStr;
		const valueSum = this.totalSumValue;
		if (valueSum === null) {
			valueStr = '';
		} else {
			valueStr = formatNumber(valueSum, this.format, { scale: this.scale });
		}
		return valueStr;
	}

	get metricNameOverride() {
		return this.dataSelect.overrides.title || '';
	}

	get isOneSpecificAttributeOnSetup() {
		return this.dataSelect.metricSetup.outputAttributesCount === 1;
	}

	@cached
	get periodEndTS(): number {
		const granulationLabels = this.granulationLabels;
		return granulationLabels[granulationLabels.length - 1]?.endTs || 0;
	}

	getOutputByDimensionValuesAndUnit(
		dimensionValues: DSData.DimensionsAndValuesMap,
		unit: string | null,
	): DsDataOutputBase | null {
		return (
			this.searchOutputsByDimensionValuesAndUnit(this.outputs, dimensionValues, unit)[0] ||
			null
		);
	}

	getOtherOutputByDimensionValuesUnitAndTimestamp(
		dimensionValues: DSData.DimensionsAndValuesMap,
		unit: string | null,
		ts = 0,
	): DsDataOutputOther | null {
		const outputs = this.searchOutputsByDimensionValuesAndUnit(
			this.outputsOther,
			dimensionValues,
			unit,
		);
		const output = outputs.find((o) => {
			return o.ts === ts;
		});
		return output || null;
	}

	private searchOutputsByDimensionValuesAndUnit<T extends DsDataOutputBase>(
		outputs: T[],
		dimensionValues: DSData.DimensionsAndValuesMap,
		unit: string | null,
	): T[] {
		return outputs.filter((o) => {
			return o.equalDimensionValues(dimensionValues) && o.unit === unit;
		});
	}
}
